import { handleApiResult } from '@store/error';
import { apiSlice, toQueryParams } from './apiSlice';

export type ActivationOutcome =
  | 'seatGranted'
  | 'seatReclaimed'
  | 'seatReleased'
  /** An administrator took the seat back from the Seat sessions page. */
  | 'seatRevoked'
  | 'seatDenied'
  | 'authFailed'
  | 'rejected'
  /** A device reported time it used on offline credit, none of it beyond the limit. */
  | 'ticketsReported'
  /** The same, with some of it used while every seat was held by somebody else. */
  | 'ticketsBeyondCapacity'
  /** An administrator changed a license's offline credit or seat time on the Licenses page. */
  | 'licenseSettingsChanged';

/** One value a `licenseSettingsChanged` event changed, in seconds. */
export interface LicenseSettingChange {
  field: 'offlineCreditAdminWindowSeconds' | 'offlineCreditAdminBudgetSeconds' | 'seatAllocationAdminTimeInSeconds';
  /** The administrator's value before and after; null when none was set, so from null is set and to null is cleared. */
  from: number | null;
  to: number | null;
  /** The license file's value at the time: the most offline credit may be, the least the seat time may be. */
  licenseFile: number | null;
  /** What devices are given after the change, and whether that is the administrator's value or the license file's. */
  effective: number | null;
  source: 'administrator' | 'licenseFile';
}

/**
 * One recorded event. Note what is absent: no device name, no IP address, no raw device id.
 * `deviceHash` is stable within a calendar month and meaningless outside it. An administrator's
 * change to a license's settings names the administrator in `actor` instead, and carries no device.
 */
export interface ActivationLogEntry {
  createdAt: string;
  outcome: ActivationOutcome;
  statusCode: number;
  failureReason?: string;
  licenseId?: string;
  activationId?: string;
  sessionId?: string;
  packageIdentifier?: string;
  deviceHash?: string;
  deviceModel?: string;
  runtimePlatform?: string;
  version?: string;
  /** How long the seat had been held. Present on release and reclaim only. */
  heldSeconds?: number;
  /** Offline tickets one report filed, their time, and how many ran beyond capacity. */
  ticketCount?: number;
  ticketSeconds?: number;
  beyondCapacityCount?: number;
  /** On `licenseSettingsChanged`: the user name of the administrator who made the change. */
  actor?: string;
  /** On `licenseSettingsChanged`: each value the change changed. */
  changes?: LicenseSettingChange[];
}

export interface ActivationLogPage {
  items: ActivationLogEntry[];
  total: number;
  page: number;
  limit: number;
}

interface FamilyCounts { supar: number, composer: number }

export interface ActivationLogSummary {
  from: string;
  to: string;
  /** Null when nothing has been sampled in the window — not the same as zero usage. */
  peakConcurrent: FamilyCounts | null;
  currentInUse: FamilyCounts | null;
  entitled: FamilyCounts;
  averageHeldSeconds: number | null;
  closedSessions: number;
  refusalsByReason: Record<string, number>;
  /** Offline credit reported in the window, counted by when it was reported. */
  offlineUsage: {
    reports: number;
    tickets: number;
    seconds: number;
    ticketsBeyondCapacity: number;
  };
}

export interface SeatUsagePoint {
  at: string;
  supar: number;
  composer: number;
}

export interface ActivationLogQuery {
  from?: string;
  to?: string;
  licenseId?: string;
  outcome?: ActivationOutcome;
  failureReason?: string;
  page?: number;
  limit?: number;
}

export const activationLogApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getActivationLogs: builder.query<ActivationLogPage, ActivationLogQuery>({
      query: (params) => ({ url: '/v1/activation-logs', params: toQueryParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
    getActivationLogSummary: builder.query<ActivationLogSummary, ActivationLogQuery>({
      query: (params) => ({ url: '/v1/activation-logs/summary', params: toQueryParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
    getSeatUsageSeries: builder.query<SeatUsagePoint[], ActivationLogQuery>({
      query: (params) => ({ url: '/v1/activation-logs/series', params: toQueryParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const {
  useGetActivationLogsQuery,
  useGetActivationLogSummaryQuery,
  useGetSeatUsageSeriesQuery,
} = activationLogApiSlice;
